import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import MainTiles from './MainTiles';
import MainCharts from './MainCharts';
import MainGrid from './MainGrid';
import MainFilter from './MainFilter';
import Tools from '../utils/Tools';
import { useFilter } from '../FilterContext';

function MainPage() {
  const location = useLocation();
  const [rowData, setRowData] = useState([]);
  const { filters, setFilters } = useFilter();
  const [loading, setLoading] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(true);

  const [dateGroupData, setDateGroupData] = useState(null);
  const [dateGroupType, setDateGroupType] = useState(null);
  const [baselines, setBaselines] = useState({});
  const [intentCounts, setIntentCounts] = useState({});
  const [categories, setCategories] = useState([]);
  const [intents, setIntents] = useState([]);
  const [minorIntents, setMinorIntents] = useState([]);
  const [queues, setQueues] = useState([]);
  const [initiationMethods, setInitiationMethods] = useState([]);
  const [agents, setAgents] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalIntents, setTotalIntents] = useState(0);
  const [totalCategories, setTotalCategories] = useState(0);
  const [topCategories, setTopCategories] = useState([]);
  const [topIntents, setTopIntents] = useState([]);
  const [showTrends, setShowTrends] = useState(false);
  const [sortModel, setSortModel] = useState([]);
  



  // Fetch summary data from server whenever filters change
  useEffect(() => {
    setLoadingSummary(true);
    const queryParams = new URLSearchParams();
    
    // Add all filter parameters to queryParams
    if (filters.category && filters.category.length > 0) {
      filters.category.forEach((cat) => queryParams.append('category', cat.value));
    }
    if (filters.intent && filters.intent.length > 0) {
      filters.intent.forEach((intent) => queryParams.append('intent', intent.value));
    }
    if (filters.agent) {
      queryParams.append('agent', filters.agent.value);
    }
    if (filters.callId) {
      queryParams.append('callId', filters.callId);
    }
    if (filters.queue) {
      queryParams.append('queue', filters.queue.value);
    }
    if (filters.initiationMethod) {
      queryParams.append('initiationMethod', filters.initiationMethod.value);
    }
    if (filters.dateRange) {
      queryParams.append('dateRange', filters.dateRange.value);
    }
    if (filters.dateStart) {
      queryParams.append('dateStart', formatDateToYYYYMMDD(filters.dateStart));
    }
    if (filters.dateEnd) {
      queryParams.append('dateEnd', formatDateToYYYYMMDD(filters.dateEnd));
    }
    if (filters.minorIntents && filters.minorIntents.length > 0) {
      filters.minorIntents.forEach((intent) => queryParams.append('minorIntents', intent.value));
    }
    if (!filters.excludeWeekends) {
      queryParams.append("excludeWeekends", filters.excludeWeekends);
    }

    Tools.sendRequest("get-summary", queryParams.toString()).then((response) => {
      console.log('DEBUG - GOT SUMMARY', response);

      // Set counts and metadata
      setCategories(
        response.uniqueCategories.map((c) => ({
          value: c,
          label: c,
          count: response.categoryCounts[c],
        }))
      );
      setIntents(
        response.uniqueIntents.map((i) => ({
          value: i,
          label: i,
          count: response.intentCounts[i],
        }))
      );
      setMinorIntents(
        Object.keys(response.minorIntentCounts).map((i) => ({
          value: i,
          label: i,
          count: response.minorIntentCounts[i],
        }))
      );
      setAgents(
        response.uniqueAgents.map((a) => ({
          value: a,
          label: a,
          count: response.agentCounts[a],
        }))
      );
      setQueues(
        Object.keys(response.callsByQueue).map((a) => ({
          value: a,
          label: a,
          count: response.callsByQueue[a],
        }))
      );
      setInitiationMethods(
        Object.keys(response.callsByInitiationMethod).map((a) => ({
          value: a,
          label: a,
          count: response.callsByInitiationMethod[a],
        }))
      );
      setTotalIntents(response.totalIntents);
      setTotalCategories(response.totalCategories);
      setTopCategories(response.topCategories);
      setTopIntents(response.topIntents);
      setDateGroupData(response.dateGroupData);
      setDateGroupType(response.dateGroupType);
      setBaselines(response.baselines);
      setIntentCounts(response.intentCounts);
      setLoadingSummary(false); // Summary data fetching is complete
    });
  }, [filters]);

  // Fetch paged data from server whenever page changes
  useEffect(() => {
    setLoading(true);
    const queryParams = new URLSearchParams();
    if (filters.category && filters.category.length > 0) {
      filters.category.forEach((cat) => queryParams.append('category', cat.value));
    }
    if (filters.intent && filters.intent.length > 0) {
      filters.intent.forEach((intent) => queryParams.append('intent', intent.value));
    }
    if (filters.agent) {
      let agentValue = filters.agent;
      if (agentValue.value) {
        agentValue = agentValue.value;
      }
      queryParams.append('agent', agentValue);
    }
    if (filters.callId) {
      queryParams.append('callId', filters.callId);
    }
    if (filters.dateRange) {
      queryParams.append('dateRange', filters.dateRange.value);
    }
    if (filters.dateStart) {
      queryParams.append('dateStart', formatDateToYYYYMMDD(filters.dateStart));
    }
    if (filters.dateEnd) {
      queryParams.append('dateEnd', formatDateToYYYYMMDD(filters.dateEnd));
    }
    if (filters.minorIntents) {
      if(Array.isArray(filters.minorIntents)) {
        filters.minorIntents.forEach((intent) => queryParams.append('minorIntents', intent.value));
      } else {
        queryParams.append('minorIntents', filters.minorIntents);
      }
    }
    if (filters.queue) {
      queryParams.append('queue', filters.queue.value);
    }
    if (filters.initiationMethod) {
      queryParams.append('initiationMethod', filters.initiationMethod.value);
    }
    if (!filters.excludeWeekends) {
      queryParams.append("excludeWeekends", filters.excludeWeekends);
    }
    queryParams.append('page', currentPage);

    // sorting parameters
    if (sortModel && sortModel.length > 0) {
      sortModel.forEach((sortItem) => {
        queryParams.append('sortBy', sortItem.colId);
        queryParams.append('sortOrder', sortItem.sort);
      });
    }

    Tools.sendRequest("get-data", queryParams.toString()).then((response) => {
      console.log('DEBUG - GOT DATA', response);
      setRowData(response.data);
      setTotalRecords(response.totalRecords);
      setTotalPages(response.totalPages);
      setCurrentPage(response.currentPage);
      setLoading(false); // Data fetching is complete
    });
  }, [currentPage, filters, sortModel]);

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  }, [setFilters]);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleSortChange = useCallback((newSortModel) => {
    setSortModel(newSortModel);
  }, []);

  return (
    <div className="container mx-auto p-4">
      <MainFilter

        setFilters={handleFilterChange}
        updateShowTrends={setShowTrends}
        categories={categories}
        intents={intents}
        agents={agents}
        initiationMethods={initiationMethods}
        queues={queues}
        minorIntents={minorIntents}
      />
      {(loading || loadingSummary) ? (
        <div className="flex justify-center items-center h-64">
          <div className="loader"></div>
        </div>
      ) : (
        <>
          <MainTiles
            callCount={totalRecords}
            agentCount={agents.length}
            totalIntents={totalIntents}
            totalCategories={totalCategories}
            baselines={baselines}
            showTrends={showTrends}
            dateRange={filters.dateRange.value}
          />
          <MainCharts
            topCategories={topCategories}
            topIntents={topIntents}
            baselines={baselines}
            intentCounts={intentCounts}
            dateGroupData={dateGroupData}
            dateGroupType={dateGroupType}
            showTrends={showTrends}
            dateRange={filters.dateRange.value}
          />
          <MainGrid
            data={rowData}
            currentPage={currentPage}
            totalPages={totalPages}
            totalRecs={totalRecords}
            onPageChange={handlePageChange}
            onSortChange={handleSortChange}
            filters={filters}
            sortModel={sortModel}  // Pass sortModel to Grid
          />
        </>
      )}
    </div>
  );
}

export default MainPage;

function formatDateToYYYYMMDD(date) {
  const d = new Date(date);
  let month = '' + (d.getMonth() + 1);
  let day = '' + d.getDate();
  const year = d.getFullYear();

  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;

  return [year, month, day].join('-');
}